import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { intelligenceApi } from '../api/intelligence'
import { INSIGHT_CATEGORIES, insightCategoryLabel } from '../api/insights'
import { ApiError } from '../lib/api'
import type { InsightProposal, InsightWeight, ManagerQuestion } from '../lib/types'
import { formatDateBR } from '../lib/format'
import { fmtExactBR, parseNumberBR, roasPercentHint } from '../lib/numberBR'
import { Spinner } from './Spinner'

/** Invalida tudo que depende do dossiê/caixa depois de uma resposta ou confirmação. */
export function useInvalidateIntelligence() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['intelligence-inbox'] })
    qc.invalidateQueries({ queryKey: ['intelligence-history'] })
    qc.invalidateQueries({ queryKey: ['client-insights'] })
    qc.invalidateQueries({ queryKey: ['knowledge'] })
    qc.invalidateQueries({ queryKey: ['client-interviews'] })
    qc.invalidateQueries({ queryKey: ['dashboard'] })
    qc.invalidateQueries({ queryKey: ['report'] })
    qc.invalidateQueries({ queryKey: ['client'] })
    // "Isto foi a ação X?" → Sim marca a ação como feita: atualiza as listas de ações.
    qc.invalidateQueries({ queryKey: ['open-actions'] })
    qc.invalidateQueries({ queryKey: ['planning'] })
    qc.invalidateQueries({ queryKey: ['impact'] })
  }
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Erro ao salvar.')

/**
 * CAIXA DA IA — perguntas objetivas e aprendizados que o curador propôs a partir do
 * retorno do gerente. Tudo em 1 clique: responder, confirmar, corrigir ou descartar.
 * `compact` = faixa no topo de outras telas (mostra só os primeiros itens).
 */
export function IntelligenceInbox({
  clientId,
  compact = false,
  limit = 3,
}: {
  clientId?: string
  compact?: boolean
  limit?: number
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['intelligence-inbox', clientId ?? 'all'],
    queryFn: () => intelligenceApi.inbox(clientId),
  })
  const questions = data?.questions ?? []
  const proposals = data?.proposals ?? []
  const total = questions.length + proposals.length

  if (isLoading) return compact ? null : <Spinner />
  if (total === 0) {
    if (compact) return null
    return (
      <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
        Nada esperando por você — a IA está em dia com o que aprendeu. 🎉
      </p>
    )
  }

  const showClient = !clientId
  const items: Array<{ kind: 'q'; q: ManagerQuestion } | { kind: 'p'; p: InsightProposal }> = [
    ...questions.map((q) => ({ kind: 'q' as const, q })),
    ...proposals.map((p) => ({ kind: 'p' as const, p })),
  ]
  const visible = compact ? items.slice(0, limit) : items

  return (
    <section
      className={`rounded-xl border ${compact ? 'border-violet-200 bg-violet-50/40 p-4' : 'border-slate-200 bg-white p-5'}`}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-slate-900">🧠 A IA quer aprender com você</h2>
          <p className="text-xs text-slate-500">
            {questions.length > 0 && `${questions.length} pergunta${questions.length === 1 ? '' : 's'} rápida${questions.length === 1 ? '' : 's'}`}
            {questions.length > 0 && proposals.length > 0 && ' · '}
            {proposals.length > 0 &&
              `${proposals.length} aprendizado${proposals.length === 1 ? '' : 's'} para confirmar`}
            {' — cada resposta vira conhecimento que a IA usa toda semana.'}
          </p>
        </div>
        {compact && total > visible.length && (
          <Link to="/intelligence" className="text-sm font-medium text-brand-600 hover:underline">
            Ver todos ({total}) →
          </Link>
        )}
      </div>
      <ul className="space-y-2">
        {visible.map((it) =>
          it.kind === 'q' ? (
            <QuestionItem key={`q-${it.q.id}`} q={it.q} showClient={showClient} />
          ) : (
            <ProposalItem key={`p-${it.p.id}`} p={it.p} showClient={showClient} />
          )
        )}
      </ul>
    </section>
  )
}

function ClientTag({ name }: { name: string }) {
  return (
    <span className="rounded-full bg-slate-900/80 px-2 py-0.5 text-[11px] font-semibold text-white">{name}</span>
  )
}

/** Pergunta objetiva: um clique na opção já responde. */
export function QuestionItem({ q, showClient = true }: { q: ManagerQuestion; showClient?: boolean }) {
  const invalidate = useInvalidateIntelligence()
  // Meta proposta pelo dono no portal: o número dele já vem preenchido (options[0]).
  const ownerProposal = q.kind === 'number' && q.topic === 'meta' && q.options.length > 0
  const [value, setValue] = useState(ownerProposal ? q.options[0] : '')
  const [note, setNote] = useState('')
  const [showNote, setShowNote] = useState(false)

  const answer = useMutation({
    mutationFn: (a: string) => intelligenceApi.answer(q.id, a, note),
    onSuccess: invalidate,
  })
  const dismiss = useMutation({ mutationFn: () => intelligenceApi.dismiss(q.id), onSuccess: invalidate })
  const busy = answer.isPending || dismiss.isPending
  // A caixa aparece dentro do <form> do cadastro do cliente: Enter não pode submetê-lo.
  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>, submit?: () => void) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    submit?.()
  }
  const submitValue = () => {
    if (value.trim() && !busy) answer.mutate(value.trim())
  }

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        {showClient && <ClientTag name={q.clientName} />}
        {q.topic === 'execucao' ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
            Isto foi feito? · histórico da conta
          </span>
        ) : ownerProposal ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
            Meta proposta pelo dono · portal
          </span>
        ) : (
          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
            Pergunta da IA
          </span>
        )}
        {q.campaign && (
          <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500">{q.campaign}</span>
        )}
      </div>
      <p className="text-sm font-medium text-slate-900">{q.question}</p>
      {q.why && <p className="mt-0.5 text-xs text-slate-500">{q.why}</p>}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {(q.kind === 'choice' || q.kind === 'yesno') &&
          q.options.map((opt) => (
            <button
              key={opt}
              type="button"
              disabled={busy}
              onClick={() => answer.mutate(opt)}
              className="rounded-full border border-brand-300 bg-white px-3 py-1 text-xs font-semibold text-brand-700 transition hover:bg-brand-600 hover:text-white disabled:opacity-50"
            >
              {opt}
            </button>
          ))}
        {(q.kind === 'number' || q.kind === 'text') && (
          <>
            {q.kind === 'number' ? (
              <input
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => onEnter(e, submitValue)}
                placeholder="Só o número"
                className="w-32 rounded-md border border-slate-300 px-2.5 py-1 text-sm focus:border-brand-500 focus:outline-none"
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => onEnter(e, submitValue)}
                placeholder="Sua resposta"
                className="min-w-[220px] flex-1 rounded-md border border-slate-300 px-2.5 py-1 text-sm focus:border-brand-500 focus:outline-none"
              />
            )}
            <button
              type="button"
              disabled={busy || !value.trim()}
              onClick={submitValue}
              className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Responder
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => answer.mutate('Não sei')}
              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Não sei
            </button>
          </>
        )}
        {busy && <Spinner className="h-4 w-4" />}
        <span className="ml-auto flex items-center gap-3 text-xs">
          <button type="button" onClick={() => setShowNote((v) => !v)} className="text-slate-500 hover:underline">
            {showNote ? 'sem comentário' : '+ comentário'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => dismiss.mutate()}
            className="text-slate-400 hover:text-slate-600 hover:underline"
          >
            não se aplica
          </button>
        </span>
      </div>
      {q.kind === 'number' && <NumberPreview value={value} metric={q.topic === 'meta' ? q.metric : null} />}
      {showNote && (
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => onEnter(e)}
          placeholder="Comentário (vai junto com a resposta)"
          className="mt-2 w-full rounded-md border border-slate-300 px-2.5 py-1 text-xs focus:border-brand-500 focus:outline-none"
        />
      )}
      {(answer.isError || dismiss.isError) && (
        <p className="mt-1 text-xs text-red-600">{errText(answer.error ?? dismiss.error)}</p>
      )}
    </li>
  )
}

/** Como o número digitado vai ser lido ("2.500" = 2500) — e o alerta de ROAS em %. */
export function NumberPreview({ value, metric }: { value: string; metric: string | null }) {
  if (!value.trim()) return null
  const n = parseNumberBR(value)
  const hint = roasPercentHint(metric, n)
  return (
    <div className="mt-1 space-y-0.5 text-xs">
      <p className={n == null ? 'text-rose-600' : 'text-slate-500'}>
        {n == null ? 'Não é um número.' : <>Vai gravar: <strong className="text-slate-800">{fmtExactBR(n)}</strong></>}
      </p>
      {hint && <p className="text-amber-700">⚠️ {hint}</p>}
    </div>
  )
}

/** Aprendizado proposto pelo curador: confirmar, corrigir ou descartar. */
export function ProposalItem({ p, showClient = true }: { p: InsightProposal; showClient?: boolean }) {
  const invalidate = useInvalidateIntelligence()
  const [editing, setEditing] = useState(false)
  const [showWhy, setShowWhy] = useState(false)
  const [content, setContent] = useState(p.content)
  const [category, setCategory] = useState(p.category)
  const [weight, setWeight] = useState<InsightWeight>(p.weight)

  const confirm = useMutation({
    mutationFn: (edits: { content?: string; category?: string; weight?: InsightWeight }) =>
      intelligenceApi.confirm(p.id, edits),
    onSuccess: invalidate,
  })
  const reject = useMutation({ mutationFn: () => intelligenceApi.reject(p.id), onSuccess: invalidate })
  const busy = confirm.isPending || reject.isPending

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        {showClient && <ClientTag name={p.clientName} />}
        {p.source === 'site' ? (
          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">Lido no site</span>
        ) : (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            A IA aprendeu
          </span>
        )}
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
          {insightCategoryLabel(p.category)}
        </span>
        {p.weight === 'absolute' && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Regra</span>
        )}
        {p.validUntil && (
          <span className="text-[11px] text-slate-400">vale até {formatDateBR(p.validUntil)}</span>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1 text-xs"
            >
              {INSIGHT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={weight === 'absolute'}
                onChange={(e) => setWeight(e.target.checked ? 'absolute' : 'context')}
                className="rounded text-brand-600"
              />
              É regra (vale mais que as métricas)
            </label>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-800">{p.content}</p>
      )}
      {p.supersedes && (
        <p className="mt-1 text-xs text-slate-500">
          <span className="font-medium">Atualiza:</span> <span className="line-through">{p.supersedes.content}</span>
        </p>
      )}
      {p.evidence && (
        <div className="mt-1">
          <button type="button" onClick={() => setShowWhy((v) => !v)} className="text-xs text-slate-500 hover:underline">
            {showWhy ? 'esconder de onde veio' : 'de onde a IA tirou isso?'}
          </button>
          {showWhy && <p className="mt-1 rounded-md bg-slate-50 px-2 py-1 text-xs italic text-slate-600">{p.evidence}</p>}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {editing ? (
          <>
            <button
              type="button"
              disabled={busy || content.trim().length < 3}
              onClick={() => confirm.mutate({ content, category, weight })}
              className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Salvar e confirmar
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => confirm.mutate({})}
              className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              ✓ Está certo
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(true)}
              className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              ✎ Corrigir
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => reject.mutate()}
              className="rounded-md border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            >
              ✗ Está errado
            </button>
          </>
        )}
        {busy && <Spinner className="h-4 w-4" />}
      </div>
      {(confirm.isError || reject.isError) && (
        <p className="mt-1 text-xs text-red-600">{errText(confirm.error ?? reject.error)}</p>
      )}
    </li>
  )
}
